import { Zap } from 'lucide-react';

/**
 * Smart Wager Hint — slim ghost-text strip designed to live under a stake
 * input. Reads the player's remaining rollover (`pending_rollover`) and
 * the user's current entry into the stake field. Shows a one-tap "Fill"
 * shortcut that auto-sets the stake to the exact remaining rollover, so
 * the player can clear their free-play wagering requirement in one bet.
 *
 * Auto-hides when:
 *   - User has no active rollover
 *   - The current stake already covers the remaining rollover
 *
 * Props:
 *   user            — auth user object (reads pending_rollover)
 *   currentAmount   — string|number, the current value of the stake input
 *   onFill(amount)  — callback invoked when user taps "Fill"; receives a
 *                     string ready to drop straight into setState
 */
const SmartWagerHint = ({ user, currentAmount, onFill }) => {
    const pending = Number(user?.pending_rollover || 0);
    if (pending <= 0) return null;

    const currentNum = Number(currentAmount || 0);
    // Hide once the current stake already clears the requirement
    if (currentNum >= pending) return null;

    // Round up to next penny so the bet always fully clears the rollover
    const target = Math.ceil(pending * 100) / 100;

    return (
        <button
            type="button"
            onClick={() => onFill && onFill(String(target))}
            className="mt-1 w-full text-left text-[10px] text-[#FFD700] hover:text-white flex items-center gap-1 transition-colors group"
            data-testid="smart-wager-hint"
        >
            <Zap className="w-3 h-3 shrink-0 text-[#FFD700] group-hover:text-white" />
            <span>
                Wager <span className="font-mono font-bold">${target.toFixed(2)}</span> to unlock your free play bonus
            </span>
            <span
                className="ml-auto px-1.5 py-0.5 rounded bg-[#FFD700]/15 border border-[#FFD700]/40 text-[#FFD700] text-[9px] font-black uppercase tracking-wider group-hover:bg-[#FFD700] group-hover:text-black"
                data-testid="smart-wager-fill"
            >
                Fill
            </span>
        </button>
    );
};

export default SmartWagerHint;
